/** Browser integration for the separate US API. No timer, venue connection,
 * automatic retry or application registration. All writes are single attempts. */
import { captureUsTime, usRequestStamp, usEvidenceNow, type UsTimeEvidence } from './timeEvidence';
import type { UsLimitBreach } from './types';

export interface UsClientSession { userId: string; orgId: string; generation: number }
export interface UsClientScope { accountId: string; scopeKey: string; revision: string }
export interface UsClientDraft {
  scope: { venue: 'polymarket_us'; scopeKey: string; revision: string };
  target: { marketSlug: string; outcome: 'yes' | 'no' };
  action: 'buy' | 'sell'; quantity: string; outcomePrice: string;
  timeInForce: string; postOnly: boolean; draftRevision: string; goodTillTime?: string;
}
export interface UsClientPreview {
  timeEvidence?: UsTimeEvidence;
  id: string; expiresAtMs: number; scopeKey: string; scopeRevision: string;
  draftRevision: string; target: UsClientDraft['target']; action: UsClientDraft['action'];
  quantity: string; outcomePrice: string; timeInForce: string; postOnly: boolean;
  goodTillTime: string | null; feeReserveUsd: string; estimatedFeeUsd: string | null;
}
export interface UsClientOrder {
  /** Absent only on legacy retail payloads. Native rows never use retail actions. */
  protocol?: 'retail' | 'institutional'; canCancel?: boolean;
  intentId: string; accountId: string; own: boolean; orderId: string | null;
  submissionState: 'ready' | 'dispatching' | 'submitted' | 'unknown' | 'accepted' | 'rejected' | 'aborted';
  reason: string | null; target: UsClientDraft['target']; action: UsClientDraft['action'];
  /** Relay market title (live catalogue, else stored at preview; #1026). null when unknown. */
  marketTitle: string | null;
  quantity: string; outcomePrice: string; timeInForce: string; postOnly: boolean;
  goodTillTime: string | null; createdAtMs: number; updatedAtMs: number;
  lifecycle: { state: string; filledQuantity: string; remainingQuantity: string | null;
    accepted: boolean; terminal: boolean; needsReconciliation: boolean; reasons: string[]; observedAtMs: number } | null;
}
export interface UsPendingRequest {
  version: 1; userId: string; orgId: string; accountId: string;
  requestKey: string; previewId: string; createdAtMs: number;
}
export type UsSubmitResult =
  /** `limit`: the trader cap a `US_RISK_LIMIT` refusal names, when the relay reported it (display only). */
  | { kind: 'receipt'; requestKey: string; order: UsClientOrder; limit?: UsLimitBreach }
  | { kind: 'unconfirmed'; requestKey: string; code: string; order?: UsClientOrder };
interface StoragePort { readonly length: number; key(index: number): string | null;
  getItem(key: string): string | null; setItem(key: string, value: string): void; removeItem(key: string): void }
export interface UsTradingClientDependencies {
  session(): UsClientSession | null;
  /** Authenticated, single-attempt same-origin transport. Must not refresh/retry writes. */
  send(path: string, init: RequestInit, session: UsClientSession): Promise<Response>;
  storage: StoragePort; uuid(): string; clock(): number;
}
export class UsClientError extends Error {
  constructor(readonly code: string) { super(code); }
}
const fail = (code = 'US_CLIENT_INVALID_RESPONSE'): never => { throw new UsClientError(code); };
const uuid = (v: unknown): v is string => typeof v === 'string' && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/.test(v);
const text = (v: unknown): v is string => typeof v === 'string' && v.length > 0 && v.length <= 256 && !/[\u0000-\u001f\u007f]/.test(v);
const record = (v: unknown): Record<string, unknown> => v && typeof v === 'object' && !Array.isArray(v) ? v as Record<string, unknown> : fail();
const time = (v: unknown): v is number => Number.isSafeInteger(v) && Number(v) >= 0;
function decimal(v: unknown): string {
  if (typeof v !== 'string' || !/^(?:0|[1-9]\d{0,11})(?:\.\d{1,12})?$/.test(v)) return fail();
  return v.includes('.') ? v.replace(/0+$/, '').replace(/\.$/, '') : v;
}
const units = (v: string) => { const [a, b = ''] = decimal(v).split('.'); return BigInt(a) * 1_000_000_000_000n + BigInt(b.padEnd(12, '0')); };
const safeCode = (v: unknown) => typeof v === 'string' && /^(?:US|RELAY)_[A-Z0-9_]{1,100}$/.test(v) ? v : 'US_REQUEST_UNCONFIRMED';
const prefix = 'g1.us-request.v1.';
const owner = (s: UsClientSession) => prefix + s.userId + '.' + s.orgId + '.';
const sameSession = (a: UsClientSession | null, b: UsClientSession) => a?.userId === b.userId && a.orgId === b.orgId && a.generation === b.generation;
/** A cap refusal's figures, for display only; anything malformed is dropped. */
function limitBreach(v: unknown): UsLimitBreach | null {
  if (!v || typeof v !== 'object' || Array.isArray(v)) return null;
  const r = v as Record<string, unknown>;
  const count = (x: unknown): x is number => typeof x === 'number' && Number.isFinite(x) && x >= 0;
  if ((r.rule !== 'LIMIT_MAX_CONTRACTS' && r.rule !== 'LIMIT_MAX_POSITIONS')
    || !count(r.cap) || !count(r.current) || !count(r.requested)) return null;
  return { rule: r.rule, cap: r.cap, current: r.current, requested: r.requested };
}

// Display only: a missing or malformed title never refuses the order (#1026).
const marketTitle = (v: unknown): string | null => typeof v === 'string' && v.trim() !== '' && v.length <= 1000
  && !/[\u0000-\u001f\u007f]/.test(v) ? v : null;
function terms(v: Record<string, unknown>) {
  const target = record(v.target);
  if (!text(target.marketSlug) || !['yes', 'no'].includes(String(target.outcome))
    || !['buy', 'sell'].includes(String(v.action)) || !text(v.timeInForce) || typeof v.postOnly !== 'boolean'
    || !(v.goodTillTime == null || (typeof v.goodTillTime === 'string' && Number.isFinite(Date.parse(v.goodTillTime))))) return fail();
  const quantity = decimal(v.quantity), outcomePrice = decimal(v.outcomePrice);
  if (units(quantity) <= 0n || units(outcomePrice) <= 0n || units(outcomePrice) >= 1_000_000_000_000n) return fail();
  return { target: { marketSlug: target.marketSlug, outcome: target.outcome as 'yes' | 'no' },
    action: v.action as 'buy' | 'sell', quantity, outcomePrice, timeInForce: v.timeInForce,
    postOnly: v.postOnly, goodTillTime: v.goodTillTime as string | null ?? null };
}
export function parseUsClientOrder(value: unknown, accountId: string, view: 'own' | 'desk' = 'own'): UsClientOrder {
  const envelope = record(value), r = record(envelope.order);
  if (envelope.venue !== 'polymarket_us' || envelope.source !== 'saved_evidence' || r.accountId !== accountId
    || typeof r.own !== 'boolean' || (view === 'own' && !r.own) || !uuid(r.intentId) || !(r.orderId === null || text(r.orderId))
    || !['ready','dispatching','submitted','unknown','accepted','rejected','aborted'].includes(String(r.submissionState))
    || !time(r.createdAtMs) || !time(r.updatedAtMs) || r.updatedAtMs < r.createdAtMs) return fail();
  if ((r.protocol !== undefined && r.protocol !== 'retail' && r.protocol !== 'institutional')
    || (r.canCancel !== undefined && typeof r.canCancel !== 'boolean')
    || (r.protocol === 'institutional' && (r.canCancel !== false
      || r.cancel != null || r.executionFees != null))) return fail();
  const t = terms(r);
  let lifecycle: UsClientOrder['lifecycle'] = null;
  if (r.lifecycle !== null) {
    const l = record(r.lifecycle);
    if (!['pending','open','partial','filled','canceled','expired','replaced','rejected','conflict'].includes(String(l.state))
      || !time(l.observedAtMs) || ![l.accepted,l.terminal,l.needsReconciliation].every(v => typeof v === 'boolean')
      || !Array.isArray(l.reasons) || l.reasons.length > 32 || l.reasons.some(v => safeCode(v) !== v)) return fail();
    const filled = decimal(l.filledQuantity), remaining = l.remainingQuantity === null ? null : decimal(l.remainingQuantity);
    if (units(filled) > units(t.quantity) || (remaining !== null && units(remaining) + units(filled) > units(t.quantity))) return fail();
    lifecycle = { state: String(l.state), filledQuantity: filled, remainingQuantity: remaining,
      accepted: l.accepted as boolean, terminal: l.terminal as boolean, needsReconciliation: l.needsReconciliation as boolean,
      reasons: [...l.reasons] as string[], observedAtMs: l.observedAtMs };
  }
  return { ...t, ...(r.protocol === undefined ? {} : { protocol: r.protocol as 'retail' | 'institutional' }),
    ...(r.canCancel === undefined ? {} : { canCancel: r.canCancel as boolean }),
    marketTitle: marketTitle(record(r.target).marketTitle), intentId: r.intentId, accountId, own: r.own as boolean, orderId: r.orderId as string | null,
    submissionState: r.submissionState as UsClientOrder['submissionState'], reason: r.reason === null ? null : safeCode(r.reason),
    createdAtMs: r.createdAtMs, updatedAtMs: r.updatedAtMs, lifecycle };
}

/** One mounted ticket controller. setScope/setDraft invalidate confirmations,
 * including A→B→A. Saved request IDs survive remount/reload; they never resend. */
export class UsTradingClient {
  private generation = 0;
  private scope: UsClientScope | null = null;
  private scopeSession: UsClientSession | null = null;
  private draft: UsClientDraft | null = null;
  private previewValue: UsClientPreview | null = null;
  private previewSequence = 0;
  private submitted: Promise<UsSubmitResult> | null = null;
  private acknowledged = new Set<string>();
  constructor(private readonly d: UsTradingClientDependencies) {}

  setScope(scope: UsClientScope | null): void {
    if (scope && (!uuid(scope.accountId) || !text(scope.scopeKey) || !text(scope.revision))) fail('US_CLIENT_INVALID_SCOPE');
    this.scope = scope ? { ...scope } : null;
    const session = this.d.session();
    this.scopeSession = scope && session ? { ...session } : null;
    this.acknowledged.clear();
    this.setDraft(null);
  }
  setDraft(draft: UsClientDraft | null): void {
    this.generation++; this.previewValue = null; this.submitted = null; this.draft = null;
    if (!draft) return;
    if (!this.scope || draft.scope.venue !== 'polymarket_us' || draft.scope.scopeKey !== this.scope.scopeKey
      || draft.scope.revision !== this.scope.revision || !text(draft.draftRevision)) fail('US_CLIENT_INVALID_SCOPE');
    const t = terms(draft as unknown as Record<string, unknown>);
    this.draft = { ...t, scope: { ...draft.scope }, draftRevision: draft.draftRevision,
      ...(t.goodTillTime ? { goodTillTime: t.goodTillTime } : {}) } as UsClientDraft;
    if (t.goodTillTime === null) delete this.draft.goodTillTime;
  }
  private context() {
    const session = this.d.session();
    if (!session || !uuid(session.userId) || !uuid(session.orgId) || !time(session.generation)) return fail('US_CLIENT_AUTH_REQUIRED');
    if (!this.scope) return fail('US_CLIENT_ACCOUNT_REQUIRED');
    if (!this.scopeSession || !sameSession(session, this.scopeSession)) return fail('US_CLIENT_CONTEXT_CHANGED');
    return { session: { ...session }, scope: { ...this.scope }, generation: this.generation };
  }
  private current(c: ReturnType<UsTradingClient['context']>): void {
    if (!sameSession(this.d.session(), c.session) || this.generation !== c.generation) fail('US_CLIENT_CONTEXT_CHANGED');
  }
  private async request(c: ReturnType<UsTradingClient['context']>, path: string, body?: unknown) {
    this.current(c);
    const started = usRequestStamp(this.d.clock);
    const response = await this.d.send('/api/polymarket-us/accounts/' + c.scope.accountId + path,
      { method: body === undefined ? 'GET' : 'POST', cache: 'no-store', redirect: 'error',
        headers: { 'Content-Type': 'application/json' }, ...(body === undefined ? {} : { body: JSON.stringify(body) }),
        signal: AbortSignal.timeout(15_000) }, c.session);
    this.current(c);
    const data: unknown = await response.json(); this.current(c);
    return { response, data, started };
  }
  async preview(): Promise<UsClientPreview> {
    const c = this.context(), draft = this.draft;
    if (!draft) return fail('US_CLIENT_DRAFT_REQUIRED');
    if (this.submitted) return fail('US_CLIENT_CONFIRMATION_CONSUMED');
    this.previewValue = null;
    const sequence = ++this.previewSequence;
    const { response, data, started } = await this.request(c, '/previews', { draft });
    if (sequence !== this.previewSequence) return fail('US_CLIENT_CONTEXT_CHANGED');
    const e = record(data);
    if (!response.ok) return fail(safeCode(e.code));
    if (!time(e.observedAtMs)) return fail();
    const timeEvidence = captureUsTime(e.observedAtMs, started, this.d.clock);
    const p = record(e.preview), t = terms(p);
    if (e.venue !== 'polymarket_us' || e.accountId !== c.scope.accountId || !uuid(p.id)
      || p.scopeKey !== c.scope.scopeKey || p.scopeRevision !== c.scope.revision || p.draftRevision !== draft.draftRevision
      || !time(p.expiresAtMs) || p.expiresAtMs <= usEvidenceNow(timeEvidence, this.d.clock())
      || JSON.stringify(t) !== JSON.stringify(terms(draft as unknown as Record<string, unknown>))) return fail();
    const preview: UsClientPreview = { ...t, timeEvidence, id: p.id, scopeKey: c.scope.scopeKey, scopeRevision: c.scope.revision,
      draftRevision: draft.draftRevision, expiresAtMs: p.expiresAtMs, feeReserveUsd: decimal(p.feeReserveUsd),
      estimatedFeeUsd: p.estimatedFeeUsd === null ? null : decimal(p.estimatedFeeUsd) };
    // Never expose the mutable confirmation used to authorize submit.
    this.previewValue = structuredClone(preview);
    return preview;
  }
  pending(): UsPendingRequest[] {
    const c = this.context(), keyPrefix = owner(c.session), rows: UsPendingRequest[] = [];
    try {
      for (let n = 0; n < this.d.storage.length; n++) {
        const key = this.d.storage.key(n);
        if (!key?.startsWith(keyPrefix)) continue;
        const raw = this.d.storage.getItem(key);
        if (!raw || raw.length > 2048) return fail('US_CLIENT_RECOVERY_STORAGE_INVALID');
        const r = record(JSON.parse(raw));
        if (r.version !== 1 || r.userId !== c.session.userId || r.orgId !== c.session.orgId || !uuid(r.accountId)
          || !uuid(r.requestKey) || !uuid(r.previewId) || key !== keyPrefix + r.requestKey || !time(r.createdAtMs)) return fail('US_CLIENT_RECOVERY_STORAGE_INVALID');
        if (r.accountId === c.scope.accountId) rows.push({ version: 1, userId: r.userId, orgId: r.orgId,
          accountId: r.accountId, requestKey: r.requestKey, previewId: r.previewId, createdAtMs: r.createdAtMs });
      }
      return rows;
    } catch { return fail('US_CLIENT_RECOVERY_STORAGE_UNAVAILABLE'); }
  }
  /** Whether a submit could be saved for recovery right now: a bound session
   * and account, readable recovery storage and fewer than 100 unchecked
   * requests for this account. Reads only; sends and writes nothing. Null when
   * it could, else the code a submit would refuse with. An edit runs this
   * before its cancel, so a refusal it can predict never costs the original. */
  canSubmit(): string | null {
    try {
      return this.pending().length >= 100 ? 'US_CLIENT_RECOVERY_QUEUE_FULL' : null;
    } catch (error) {
      return error instanceof UsClientError ? error.code : 'US_CLIENT_RECOVERY_STORAGE_UNAVAILABLE';
    }
  }
  submit(): Promise<UsSubmitResult> {
    const c = this.context(), preview = this.previewValue;
    if (this.submitted) return this.submitted;
    if (!preview || preview.expiresAtMs <= usEvidenceNow(preview.timeEvidence, this.d.clock())) return Promise.reject(new UsClientError('US_CLIENT_PREVIEW_REQUIRED'));
    const requestKey = this.d.uuid();
    if (!uuid(requestKey)) return Promise.reject(new UsClientError('US_CLIENT_INVALID_REQUEST_KEY'));
    const pending: UsPendingRequest = { version: 1, userId: c.session.userId, orgId: c.session.orgId,
      accountId: c.scope.accountId, requestKey, previewId: preview.id, createdAtMs: this.d.clock() };
    try {
      if (this.pending().length >= 100) return Promise.reject(new UsClientError('US_CLIENT_RECOVERY_QUEUE_FULL'));
      const key = owner(c.session) + requestKey;
      if (this.d.storage.getItem(key) !== null) return Promise.reject(new UsClientError('US_CLIENT_REQUEST_KEY_REUSED'));
      const encoded = JSON.stringify(pending);
      this.d.storage.setItem(key, encoded);
      if (this.d.storage.getItem(key) !== encoded) return Promise.reject(new UsClientError('US_CLIENT_RECOVERY_STORAGE_UNAVAILABLE'));
    } catch { return Promise.reject(new UsClientError('US_CLIENT_RECOVERY_STORAGE_UNAVAILABLE')); }
    this.previewValue = null;
    this.submitted = (async (): Promise<UsSubmitResult> => {
      try {
        const { response, data } = await this.request(c, '/orders', { previewId: preview.id, requestKey });
        let order: UsClientOrder | undefined;
        try {
          order = parseUsClientOrder(data, c.scope.accountId);
          if (JSON.stringify(terms(order as unknown as Record<string, unknown>)) !== JSON.stringify(terms(preview as unknown as Record<string, unknown>))) order = undefined;
        } catch { /* Keep the original request pending. */ }
        if (order) this.acknowledged.add(requestKey);
        if (response.ok && order) return { kind: 'receipt', requestKey, order };
        // A parsed, exactly matching terminal refusal is durable evidence. An
        // HTTP status or error code alone must never erase pending recovery.
        if (order?.submissionState === 'aborted' && order.orderId === null) {
          const limit = order.reason === 'US_RISK_LIMIT' ? limitBreach(record(data).limit) : null;
          return { kind:'receipt', requestKey, order, ...(limit ? { limit } : {}) };
        }
        if (response.status === 409 && record(data).code === 'US_ORDER_NOT_SENT'
          && order?.submissionState === 'ready' && order.orderId === null) {
          return { kind: 'receipt', requestKey, order };
        }
        return { kind: 'unconfirmed', requestKey, code: safeCode(record(data).code), ...(order ? { order } : {}) };
      } catch (error) {
        this.current(c); // An old account's late outcome must not paint into a new ticket.
        return { kind: 'unconfirmed', requestKey, code: error instanceof UsClientError ? error.code : 'US_REQUEST_UNCONFIRMED' };
      }
    })();
    return this.submitted;
  }
  async recover(requestKey: string): Promise<UsSubmitResult> {
    const c = this.context();
    if (!uuid(requestKey) || !this.pending().some(r => r.requestKey === requestKey)) return fail('US_CLIENT_REQUEST_NOT_FOUND');
    try {
      const { response, data } = await this.request(c, '/order-requests/' + requestKey);
      if (!response.ok) return { kind: 'unconfirmed', requestKey, code: safeCode(record(data).code) };
      const order = parseUsClientOrder(data, c.scope.accountId); this.acknowledged.add(requestKey);
      return { kind: 'receipt', requestKey, order };
    } catch (error) {
      this.current(c);
      return { kind: 'unconfirmed', requestKey, code: error instanceof UsClientError ? error.code : 'US_REQUEST_UNCONFIRMED' };
    }
  }
  /** The UI can dismiss recovery metadata only after observing a server receipt.
   * The durable server order stays in orders/history, including unknown states. */
  acknowledge(requestKey: string): void {
    const c = this.context();
    if (!this.acknowledged.has(requestKey) || !this.pending().some(r => r.requestKey === requestKey)) return fail('US_CLIENT_RECEIPT_REQUIRED');
    try { this.d.storage.removeItem(owner(c.session) + requestKey); }
    catch { return fail('US_CLIENT_RECOVERY_STORAGE_UNAVAILABLE'); }
    this.acknowledged.delete(requestKey);
  }
}
